import { type ComponentProps, type ReactElement, createElement } from "react"

import eq from "lodash-es/eq"
import includes from "lodash-es/includes"

import { THEME } from "@/3514/theme"
import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import { VerticalProgressBarTooltip as Tooltip } from "./vertical-progress-bar-tooltip.component"
import { verticalProgressBarConfig as config } from "./vertical-progress-bar.config"
import {
    EVerticalProgressBarFillLevel as EFillLevel,
    EVerticalProgressBarColorStatus as EStatus,
    EVerticalProgressBarVariant as EVariant,
    type TVerticalProgressBarPieceRenderConfig as TPieceRenderConfig
} from "./vertical-progress-bar.types"

function getStarFillColor(fillLevel: EFillLevel): string {
    return eq(fillLevel, EFillLevel.ThreePiecesFilled) ? config.VERTICAL_PROGRESS_BAR_STAR_FILLED_COLOR : "none"
}

function getStarStroke(fillLevel: EFillLevel): { strokeColor: string; strokeOpacity: number } {
    return eq(fillLevel, EFillLevel.ThreePiecesFilled)
        ? { strokeColor: THEME.COLORS.black, strokeOpacity: 0.4 }
        : { strokeColor: THEME.COLORS.gray[23], strokeOpacity: 1 }
}

function getContainerClasses(variant: EVariant, wrapperClassName: string): string {
    return buildCssClassName(
        "flex",
        "flex-col",
        "w-full",
        "items-center",
        "h-auto",
        "group",
        "relative",
        [
            includes<EVariant>([EVariant.FullBar, EVariant.CroppedBar], variant) && [
                "min-h-[50px]",
                eq(variant, EVariant.FullBar) && "md:min-h-[105px]"
            ]
        ],
        [
            includes<EVariant>([EVariant.FullBar, EVariant.CroppedBar], variant)
                ? ["min-w-[8px]", eq(variant, EVariant.FullBar) && "md:min-w-[24px]"]
                : "max-w-[40px]"
        ],
        getTransitionTwClasses("all", 100),
        {
            [wrapperClassName]: wrapperClassName
        }
    )
}

function _getBasePieceClasses({
    variant,
    status,
    isMobile,
    isFilled,
    isAccessible,
    isActionAffected,
    color
}: Partial<TPieceRenderConfig>): string {
    return buildCssClassName(
        "relative w-full",
        includes<EVariant>([EVariant.FullBar, EVariant.CroppedBar], variant) && [
            isFilled ? { [color]: color } : void 0,
            !isFilled ? "bg-gray-400" : void 0,
            !isFilled && !isActionAffected
                ? [
                      (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                          ? "bg-white"
                          : "bg-gray-500"
                  ]
                : void 0,
            !isFilled && isActionAffected
                ? [(eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar)) && "bg-white"]
                : void 0
        ],
        eq(variant, EVariant.Standalone) && [isFilled ? { [color]: color } : "bg-gray-500"],
        isAccessible ? "cursor-pointer" : "cursor-auto",
        eq(status, EStatus.Expired) && isFilled && "opacity-50",
        getTransitionTwClasses("all", 100)
    )
}

function renderTooltip(shouldRender: boolean): (tooltipProps: ComponentProps<typeof Tooltip>) => ReactElement | null {
    return shouldRender
        ? (props: ComponentProps<typeof Tooltip>): ReactElement => createElement(Tooltip, props)
        : (_props: ComponentProps<typeof Tooltip>): null => null
}

function generatePiecesClasses(): {
    firstPiece(config: TPieceRenderConfig): string
    secondPiece(config: TPieceRenderConfig): string
    thirdPiece(config: TPieceRenderConfig): string
    thirdPieceBottom(config: TPieceRenderConfig): string
} {
    return {
        firstPiece: ({
            variant,
            status,
            withoutStar,
            isMobile,
            isHidden: hidden,
            isFilled,
            isAccessible,
            isBarFullHeight,
            isActionAffected,
            color
        }: TPieceRenderConfig): string =>
            buildCssClassName(
                "rounded-tl-[7px] rounded-tr-[7px] flex items-center justify-center",
                eq(variant, EVariant.Standalone) ? "md:mt-auto" : "mt:auto",
                eq(variant, EVariant.Standalone)
                    ? "border-b-[2px] border-b-white"
                    : [
                          !(eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar)) &&
                              "border-b-[1px] border-b-white group-hover:border-b-blue-600"
                      ],
                withoutStar || isBarFullHeight
                    ? [
                          (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                              ? [eq(variant, EVariant.Standalone) ? "min-h-[35px]" : "min-h-[15px]"]
                              : "min-h-[35px]"
                      ]
                    : [isMobile || eq(variant, EVariant.CroppedBar) ? "min-h-[15px]" : "md:min-h-[22px]"],
                { hidden },
                _getBasePieceClasses({ variant, status, isMobile, isFilled, isAccessible, isActionAffected, color })
            ),
        secondPiece: ({
            variant,
            status,
            isMobile,
            isHidden: hidden,
            isFilled,
            isAccessible,
            isActionAffected,
            color
        }: TPieceRenderConfig): string =>
            buildCssClassName(
                [
                    [
                        (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                            ? "min-h-[15px]"
                            : "min-h-[45px]"
                    ],
                    "relative",
                    "h-full",
                    "basis-[calc(50%-17px)]"
                ],
                eq(variant, EVariant.Standalone)
                    ? [
                          "border-b-[2px] border-b-white group-hover:border-b-blue-600",
                          eq(status, EStatus.NotStarted) ? "min-h-[40px]" : "min-h-[55px] md:max-h-[55px]"
                      ]
                    : [
                          (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                              ? "border-b-[0px]"
                              : "border-b-[1px] border-b-white group-hover:border-b-blue-600"
                      ],
                { hidden },
                _getBasePieceClasses({ variant, status, isMobile, isFilled, isAccessible, isActionAffected, color })
            ),
        thirdPiece: ({
            variant,
            status,
            isMobile,
            isHidden,
            isFilled,
            isAccessible,
            isBarFullHeight,
            isActionAffected,
            color
        }: TPieceRenderConfig): string =>
            buildCssClassName(
                isBarFullHeight
                    ? isHidden
                        ? "rounded-tl-[10px] rounded-tr-[10px] mt-auto"
                        : [
                              [
                                  (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                                      ? ["min-h-[15px]", isFilled && "mb-[5px]"]
                                      : "min-h-[45px]"
                              ],
                              "h-full",
                              "rounded-bl-[3px]",
                              "rounded-br-[3px]"
                          ]
                    : [
                          [
                              (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                                  ? "min-h-[15px]"
                                  : [
                                        eq(variant, EVariant.Standalone) &&
                                            (eq(status, EStatus.NotStarted)
                                                ? "min-h-[40px]"
                                                : "min-h-[55px] md:max-h-[55px]")
                                    ]
                          ],
                          "flex",
                          "h-full",
                          "basis-[calc(50%-17px)]",
                          "rounded-bl-[3px]",
                          "rounded-br-[3px]"
                      ],
                _getBasePieceClasses({ variant, status, isFilled, isAccessible, isActionAffected, color })
            ),
        thirdPieceBottom: ({
            variant,
            isMobile,
            isBottomRounded,
            isHidden: hidden,
            isFilled,
            isBarFullHeight,
            color
        }: TPieceRenderConfig): string =>
            buildCssClassName(
                "w-full",
                isBottomRounded && "rounded-br-[3px] rounded-bl-[3px]",
                isFilled && [
                    (eq(variant, EVariant.FullBar) ? isMobile : eq(variant, EVariant.CroppedBar))
                        ? ["rounded-tl-[7px]", "rounded-tr-[7px]", isBarFullHeight && "mb-[5px]"]
                        : "rounded-tl-[10px] rounded-tr-[10px]",
                    "absolute bottom-0 top-auto"
                ],
                getTransitionTwClasses("all", 100),
                [!color ? [isMobile ? "bg-gray-300" : "bg-gray-400"] : color],
                [eq(variant, EVariant.Standalone) ? "h-[10px]" : "h-[15px]"],
                { hidden }
            )
    }
}

const utils = { getContainerClasses, getStarFillColor, getStarStroke, renderTooltip, generatePiecesClasses }

export { utils as verticalProgressBarUtils }
